import axiosInstance from './axiosInstance';

/**
 * API Helper Functions for JWT Authentication with Axios
 */

/**
 * Get JWT token from WordPress localized data
 */
export const getJwtToken = () => {
    if (typeof window.octopusReactData !== 'undefined') {
        return window.octopusReactData.jwtToken;
    }
    return null;
};

/**
 * Check if user is admin
 */
export const isAdmin = () => {
    if (typeof window.octopusReactData !== 'undefined') {
        return window.octopusReactData.isAdmin;
    }
    return false;
};
/**
 * Get current user ID
 */
export const getUserId = () => {
    if (typeof window.octopusReactData !== 'undefined') {
        return window.octopusReactData.userId;
    }
    return null;
};

/**
 * Check if user is logged in
 */
export const isUserLoggedIn = () => {
    if (typeof window.octopusReactData !== 'undefined') {
        return window.octopusReactData.isLoggedIn;
    }
    return false;
};

/**
 * Get API URL
 */
export const getApiUrl = () => {
    if (typeof window.octopusReactData !== 'undefined') {
        return window.octopusReactData.apiUrl;
    }
    return '';
};

/**
 * Get site URL
 */
export const getSiteUrl = () => {
    if (typeof window.octopusReactData !== 'undefined') {
        return window.octopusReactData.siteUrl;
    }
    return '';
};

/**
 * Make authenticated API request with JWT token using Axios
 * @param {string} endpoint - API endpoint (without base URL)
 * @param {object} config - Axios request config
 */
export const apiRequest = async (endpoint, config = {}) => {
    try {
        const response = await axiosInstance.request({
            url: endpoint,
            ...config,
        });
        return response;
    } catch (error) {
        console.error('API Request Error:', error);
        throw error;
    }
};

/**
 * GET request
 */
export const apiGet = async (endpoint, config = {}) => {
    return await axiosInstance.get(endpoint, config);
};

/**
 * POST request
 */
export const apiPost = async (endpoint, data = {}, config = {}) => {
    return await axiosInstance.post(endpoint, data, config);
};

/**
 * PUT request
 */
export const apiPut = async (endpoint, data = {}, config = {}) => {
    return await axiosInstance.put(endpoint, data, config);
};

/**
 * DELETE request
 */
export const apiDelete = async (endpoint, config = {}) => {
    return await axiosInstance.delete(endpoint, config);
};

/**
 * Get current user info using JWT token
 */
export const getUserInfo = async () => {
    try {
        const data = await apiGet('user-info');
        return data;
    } catch (error) {
        console.error('Get user info error:', error);
        throw error;
    }
};

/**
 * Verify JWT token
 */
export const verifyToken = async (token = null) => {
    const tokenToVerify = token || getJwtToken();
    
    if (!tokenToVerify) {
        return false;
    }
    
    try {
        const data = await apiPost('verify-token', { token: tokenToVerify });
        return data.valid || false;
    } catch (error) {
        console.error('Token verification error:', error);
        return false;
    }
};

/**
 * Refresh JWT token
 */
export const refreshToken = async (token = null) => {
    const tokenToRefresh = token || getJwtToken();
    
    if (!tokenToRefresh) {
        return null;
    }
    
    try {
        const data = await apiPost('refresh-token', { token: tokenToRefresh });
        return data.token || null;
    } catch (error) {
        console.error('Token refresh error:', error);
        return null;
    }
};

/**
 * Fetch user data by JWT token
 * @param {string} token - JWT token (optional, uses stored token if not provided)
 */
export const fetchUserByToken = async (token = null) => {
    try {
        const tokenToUse = token || getJwtToken();
        
        if (!tokenToUse) {
            throw new Error('No JWT token available');
        }
        
        // If token is provided, temporarily set it for this request
        const config = token ? {
            headers: {
                Authorization: `Bearer ${token}`
            }
        } : {};

        const apiRoot = getApiUrl() || 'https://octopusworkapp.com:9001';
        const userData = await apiGet(`${apiRoot}/auth/user`, config);
        return userData?.data?.user || null;
    } catch (error) {
        console.error('Fetch user by token error:', error);
        throw error;
    }
};

/**
 * Fetch conversations/message threads from external API with search and pagination
 * @param {object} params - Query parameters
 * @param {string} params.search - Search query
 * @param {number} params.limit - Number of conversations per page
 * @param {number} params.page - Page number
 */
export const getMessages = async ({ search = '', limit = 20, page = 1 } = {}) => {
    try {
        const token = getJwtToken();
        
        if (!token) {
            throw new Error('No JWT token available');
        }
        
        // Build query string
        const params = new URLSearchParams({
            search,
            limit: limit.toString(),
            page: page.toString(),
        }).toString();
        
        const apiRoot = getApiUrl() || 'https://octopusworkapp.com:9001';
        const url = `${apiRoot}/messages/getMessages?${params}`;
        
        const response = await apiGet(url);
        return response;
    } catch (error) {
        console.error('Get messages/conversations error:', error);
        throw error;
    }
};

/**
 * Fetch unread message count from API
 */
export const fetchUnreadMessage = async () => {
    try {
        const token = getJwtToken();
        if (!token) return { data: { count: 0 } };

        const apiRoot = getApiUrl() || 'https://octopusworkapp.com:9001';
        const url = `${apiRoot}/messages/fetchUnreadMessage`;
        const response = await apiGet(url);
        return response;
    } catch (error) {
        console.error('Fetch unread message count error:', error);
        return { data: { count: 0 } };
    }
};

/**
 * Fetch projects from API with search, filter, category, and pagination
 * @param {object} params - Query parameters
 * @param {string} params.search - Search query
 * @param {string} params.filter - Filter (active, completed, favourite, all)
 * @param {number} params.category - Category ID
 * @param {number} params.limit - Number of projects per page
 * @param {number} params.page - Page number
 */
export const getProjects = async ({ search = '', filter = 'all', category = '', limit = 20, page = 1 } = {}) => {
    try {
        const token = getJwtToken();
        
        if (!token) {
            throw new Error('No JWT token available');
        }
        
        // Build query string
        const params = new URLSearchParams({
            search,
            status: filter,
            limit: limit.toString(),
            page: page.toString(),
        });
        
        // Add category if provided
        if (category) {
            params.append('category', category.toString());
        }
        
        const apiRoot = getApiUrl() || 'https://octopusworkapp.com:9001';
        const url = `${apiRoot}/projects/getMyProjects?${params.toString()}`;
        
        const response = await apiGet(url);
        return response;
    } catch (error) {
        console.error('Get projects error:', error);
        throw error;
    }
};

/**
 * Fetch project categories from API
 */
export const getProjectCategories = async () => {
    try {
        const token = getJwtToken();
        
        if (!token) {
            throw new Error('No JWT token available');
        }
        
        const apiRoot = getApiUrl() || 'https://octopusworkapp.com:9001';
        const url = `${apiRoot}/projects/getCategories`;
        
        const response = await apiGet(url);
        return response;
    } catch (error) {
        console.error('Get project categories error:', error);
        throw error;
    }
};

/**
 * Get all categories from API
 */
export const getCategories = async () => {
    try {
        const token = getJwtToken();
        
        if (!token) {
            throw new Error('No JWT token available');
        }
        
        const apiRoot = getApiUrl() || 'https://octopusworkapp.com:9001';
        const url = `${apiRoot}/projects/getAdminCategories`;
        
        const response = await apiGet(url);
        return response;
    } catch (error) {
        console.error('Get categories error:', error);
        throw error;
    }
};

/**
 * Create a new category
 * @param {object} categoryData - Category data
 * @param {string} categoryData.title - Category title
 * @param {string} categoryData.description - Category description (optional)
 * @param {string} categoryData.categorible_type - Category type (optional)
 */
export const createCategory = async (categoryData) => {
    try {
        const token = getJwtToken();
        
        if (!token) {
            throw new Error('No JWT token available');
        }
        
        const apiRoot = getApiUrl() || 'https://octopusworkapp.com:9001';
        const url = `${apiRoot}/projects/createCategory`;
        
        const response = await apiPost(url, categoryData);
        return response;
    } catch (error) {
        console.error('Create category error:', error);
        throw error;
    }
};

/**
 * Update a category
 * @param {number} categoryId - Category ID
 * @param {object} categoryData - Category data
 * @param {string} categoryData.title - Category title
 * @param {string} categoryData.description - Category description (optional)
 * @param {string} categoryData.categorible_type - Category type (optional)
 */
export const updateCategory = async (categoryId, categoryData) => {
    try {
        const token = getJwtToken();
        
        if (!token) {
            throw new Error('No JWT token available');
        }
        
        const apiRoot = getApiUrl() || 'https://octopusworkapp.com:9001';
        const url = `${apiRoot}/projects/updateCategory`;
        
        const response = await apiPost(url, {
            id: categoryId,
            ...categoryData
        });
        return response;
    } catch (error) {
        console.error('Update category error:', error);
        throw error;
    }
};

/**
 * Delete a category
 * @param {number} categoryId - Category ID
 */
export const deleteCategory = async (categoryId) => {
    try {
        const token = getJwtToken();
        
        if (!token) {
            throw new Error('No JWT token available');
        }
        
        const apiRoot = getApiUrl() || 'https://octopusworkapp.com:9001';
        const url = `${apiRoot}/projects/deleteCategory`;
        
        const response = await apiPost(url, {
            id: categoryId
        });
        return response;
    } catch (error) {
        console.error('Delete category error:', error);
        throw error;
    }
};

/**
 * Toggle favorite status for a project
 * @param {number} projectId - Project ID
 * @param {boolean} isFavourite - Whether to set as favorite (true) or remove from favorites (false)
 */
export const toggleProjectFavorite = async (projectId, isFavourite) => {
    try {
        const token = getJwtToken();
        
        if (!token) {
            throw new Error('No JWT token available');
        }
        
        const apiRoot = getApiUrl() || 'https://octopusworkapp.com:9001';
        const url = `${apiRoot}/projects/favourite_project`;
        
        const response = await apiPost(url, {
            id: projectId,
            favourite: isFavourite
        });
        
        return response;
    } catch (error) {
        console.error('Toggle project favorite error:', error);
        throw error;
    }
};

/**
 * Update project status
 * @param {number} projectId - Project ID
 * @param {number} status - Project status (0 = incomplete/active, 1 = complete)
 */
export const updateProjectStatus = async (projectId, status) => {
    try {
        const token = getJwtToken();
        
        if (!token) {
            throw new Error('No JWT token available');
        }
        
        const apiRoot = getApiUrl() || 'https://octopusworkapp.com:9001';
        const url = `${apiRoot}/projects/updateProjectStatus`;
        
        const response = await apiPost(url, {
            id: projectId,
            status: status
        });
        
        return response;
    } catch (error) {
        console.error('Update project status error:', error);
        throw error;
    }
};

/**
 * Create a new project
 * @param {object} projectData - Project data
 * @param {string} projectData.title - Project title (required)
 * @param {string} projectData.description - Project description (optional)
 * @param {number} projectData.category - Category ID (optional)
 * @param {array} projectData.assignees - Array of user IDs to assign (optional)
 */
export const createProject = async (projectData) => {
    try {
        const token = getJwtToken();
        
        if (!token) {
            throw new Error('No JWT token available');
        }
        
        const apiRoot = getApiUrl() || 'https://octopusworkapp.com:9001';
        const url = `${apiRoot}/projects/createProject`;
        
        const response = await apiPost(url, projectData);
        return response;
    } catch (error) {
        console.error('Create project error:', error);
        throw error;
    }
};

/**
 * Update a project
 * @param {number} projectId - Project ID
 * @param {object} projectData - Project data to update
 * @param {string} projectData.title - Project title (optional)
 * @param {string} projectData.description - Project description (optional)
 * @param {number} projectData.category - Category ID (optional)
 */
export const updateProject = async (projectId, projectData) => {
    try {
        const token = getJwtToken();
        
        if (!token) {
            throw new Error('No JWT token available');
        }
        
        const apiRoot = getApiUrl() || 'https://octopusworkapp.com:9001';
        const url = `${apiRoot}/projects/updateProject`;
        
        const response = await apiPost(url, {
            id: projectId,
            ...projectData
        });
        return response;
    } catch (error) {
        console.error('Update project error:', error);
        throw error;
    }
};

/**
 * Delete a project
 * @param {number} projectId - Project ID
 */
export const deleteProject = async (projectId) => {
    try {
        const token = getJwtToken();
        
        if (!token) {
            throw new Error('No JWT token available');
        }
        
        const apiRoot = getApiUrl() || 'https://octopusworkapp.com:9001';
        const url = `${apiRoot}/projects/deleteProject`;
        
        const response = await apiPost(url, {
            id: projectId
        });
        return response;
    } catch (error) {
        console.error('Delete project error:', error);
        throw error;
    }
};

/**
 * Remove a user from a project
 * @param {number} projectId - Project ID
 * @param {number} userId - User ID to remove
 */
export const removeUserFromProject = async (projectId, userId) => {
    try {
        const token = getJwtToken();
        
        if (!token) {
            throw new Error('No JWT token available');
        }
        
        const apiRoot = getApiUrl() || 'https://octopusworkapp.com:9001';
        const url = `${apiRoot}/projects/removeUserFromProject`;
        
        const response = await apiPost(url, {
            project_id: projectId,
            user_id: userId
        });
        return response;
    } catch (error) {
        console.error('Remove user from project error:', error);
        throw error;
    }
};

/**
 * Add a user to a project
 * @param {number} projectId - Project ID
 * @param {number} userId - User ID to add
 */
export const addUserToProject = async (projectId, userId) => {
    try {
        const token = getJwtToken();
        
        if (!token) {
            throw new Error('No JWT token available');
        }
        
        const apiRoot = getApiUrl() || 'https://octopusworkapp.com:9001';
        const url = `${apiRoot}/projects/addUserToProject`;
        
        const response = await apiPost(url, {
            project_id: projectId,
            user_id: userId
        });
        return response;
    } catch (error) {
        console.error('Add user to project error:', error);
        throw error;
    }
};

/**
 * Get single project by ID
 * @param {number} projectId - Project ID
 */
export const getProject = async (projectId) => {
    try {
        const token = getJwtToken();
        
        if (!token) {
            throw new Error('No JWT token available');
        }
        
        const apiRoot = getApiUrl() || 'https://octopusworkapp.com:9001';
        const url = `${apiRoot}/projects/getProjectDetails/${projectId}`;
        
        const response = await apiGet(url);
        return response;
    } catch (error) {
        console.error('Get project error:', error);
        throw error;
    }
};

/**
 * Get task lists for a project
 * @param {number} projectId - Project ID
 */
export const getTaskLists = async (projectId) => {
    try {
        const token = getJwtToken();
        
        if (!token) {
            throw new Error('No JWT token available');
        }
        
        const apiRoot = getApiUrl() || 'https://octopusworkapp.com:9001';
        const url = `${apiRoot}/tasks/getTaskLists/${projectId}`;
        
        const response = await apiGet(url);
        return response;
    } catch (error) {
        console.error('Get task lists error:', error);
        throw error;
    }
};

/**
 * Create a task list
 * @param {number} projectId - Project ID
 * @param {object} taskListData - Task list data
 * @param {string} taskListData.title - Task list title
 * @param {string} taskListData.description - Task list description (optional)
 */
export const createTaskList = async (projectId, taskListData) => {
    try {
        const token = getJwtToken();
        
        if (!token) {
            throw new Error('No JWT token available');
        }
        
        const apiRoot = getApiUrl() || 'https://octopusworkapp.com:9001';
        const url = `${apiRoot}/tasks/createTaskList`;
        
        const response = await apiPost(url, {
            project_id: projectId,
            ...taskListData
        });
        return response;
    } catch (error) {
        console.error('Create task list error:', error);
        throw error;
    }
};

/**
 * Update a task list
 * @param {number} projectId - Project ID
 * @param {number} taskListId - Task list ID
 * @param {object} taskListData - Task list data
 * @param {string} taskListData.title - Task list title
 * @param {string} taskListData.description - Task list description (optional)
 */
export const updateTaskList = async (projectId, taskListId, taskListData) => {
    try {
        const token = getJwtToken();
        
        if (!token) {
            throw new Error('No JWT token available');
        }
        
        const apiRoot = getApiUrl() || 'https://octopusworkapp.com:9001';
        const url = `${apiRoot}/tasks/updateTaskList`;
        
        const response = await apiPut(url, {
            project_id: projectId,
            task_list_id: taskListId,
            ...taskListData
        });
        return response;
    } catch (error) {
        console.error('Update task list error:', error);
        throw error;
    }
};

/**
 * Delete a task list
 * @param {number} projectId - Project ID
 * @param {number} taskListId - Task list ID
 */
export const deleteTaskList = async (projectId, taskListId) => {
    try {
        const token = getJwtToken();
        
        if (!token) {
            throw new Error('No JWT token available');
        }
        
        const apiRoot = getApiUrl() || 'https://octopusworkapp.com:9001';
        const url = `${apiRoot}/tasks/${projectId}/${taskListId}/deleteTaskList`;
        
        const response = await apiPost(url, {});
        return response;
    } catch (error) {
        console.error('Delete task list error:', error);
        throw error;
    }
};

/**
 * Get tasks for a project or task list
 * @param {number} projectId - Project ID
 * @param {number} taskListId - Task list ID (optional)
 */
export const getTaskByListId = async (projectId, taskListId = null) => {
    try {
        const token = getJwtToken();
        
        if (!token) {
            throw new Error('No JWT token available');
        }
        
        const apiRoot = getApiUrl() || 'https://octopusworkapp.com:9001';
        let url = `${apiRoot}/tasks/${projectId}/${taskListId}/getTaskByListId`;
        
        if (taskListId) {
            url += `?list_id=${taskListId}`;
        }
        
        const response = await apiGet(url);
        return response;
    } catch (error) {
        console.error('Get tasks error:', error);
        throw error;
    }
};

/**
 * Create a task
 * @param {number} projectId - Project ID
 * @param {object} taskData - Task data
 * @param {string} taskData.title - Task title
 * @param {number} taskData.task_list_id - Task list ID
 * @param {string} taskData.description - Task description (optional)
 */
export const createTask = async (projectId, taskData) => {
    try {
        const token = getJwtToken();
        
        if (!token) {
            throw new Error('No JWT token available');
        }
        
        const apiRoot = getApiUrl() || 'https://octopusworkapp.com:9001';
        const url = `${apiRoot}/tasks/${projectId}/createTask`;
        
        const response = await apiPost(url, taskData);
        return response;
    } catch (error) {
        console.error('Create task error:', error);
        throw error;
    }
};

/**
 * Update a task
 * @param {number} projectId - Project ID
 * @param {number} taskId - Task ID
 * @param {object} taskData - Task data to update
 */
export const updateTask = async (projectId, taskId, taskData) => {
    try {
        const token = getJwtToken();
        
        if (!token) {
            throw new Error('No JWT token available');
        }
        
        const apiRoot = getApiUrl() || 'https://octopusworkapp.com:9001';
        const url = `${apiRoot}/tasks/${projectId}/${taskId}/update`;
        
        const response = await apiPut(url, taskData);
        return response;
    } catch (error) {
        console.error('Update task error:', error);
        throw error;
    }
};

/**
 * Update task order
 * @param {number} projectId - Project ID
 * @param {number} taskListId - Task List ID
 * @param {Array} taskOrders - Array of task orders [{id: taskId, order: orderIndex}, ...]
 */
export const updateTaskOrder = async (projectId, taskListId, taskOrders) => {
    try {
        const token = getJwtToken();
        
        if (!token) {
            throw new Error('No JWT token available');
        }
        
        const apiRoot = getApiUrl() || 'https://octopusworkapp.com:9001';
        const url = `${apiRoot}/tasks/${projectId}/${taskListId}/updateTaskOrder`;
        
        const response = await apiPut(url, { task_orders: taskOrders });
        return response;
    } catch (error) {
        console.error('Update task order error:', error);
        throw error;
    }
};

/**
 * Add an assignee to a task
 * @param {number} projectId - Project ID
 * @param {number} taskId - Task ID
 * @param {number} assigneeId - Assignee ID
 */
export const addAssigneeToTask = async (projectId, taskId, assigneeId) => {
    try {
        const token = getJwtToken();    
        if (!token) {
            throw new Error('No JWT token available');
        }
        const apiRoot = getApiUrl() || 'https://octopusworkapp.com:9001';
        const url = `${apiRoot}/tasks/${projectId}/${taskId}/addAssignee`;
        const response = await apiPost(url, { assignee_id: assigneeId });
        return response;
    } catch (error) {   
        console.error('Add assignee to task error:', error);
        throw error;
    }
};
/**
 * Get a single task by ID
 * @param {number} projectId - Project ID
 * @param {number} taskId - Task ID
 */
export const getTaskById = async (projectId, taskId) => {
    try {
        const token = getJwtToken();
        
        if (!token) {
            throw new Error('No JWT token available');
        }
        
        const apiRoot = getApiUrl() || 'https://octopusworkapp.com:9001';
        const url = `${apiRoot}/tasks/${projectId}/${taskId}/getTaskById`;
        
        const response = await apiGet(url);
        return response;
    } catch (error) {
        console.error('Get task by id error:', error);
        throw error;
    }
};

/**
 * Get my tasks (all tasks assigned to current user)
 * @param {object} params - Query parameters
 * @param {string} params.filter - Filter (active, completed, favourite, all)
 * @param {string} params.search - Search query
 * @param {number} params.limit - Number of tasks per page
 * @param {number} params.page - Page number
 */
export const getMyTasks = async ({ filter = 'all', search = '', limit = 50, page = 1 } = {}) => {
    try {
        const token = getJwtToken();
        
        if (!token) {
            throw new Error('No JWT token available');
        }
        
        // Build query string
        const params = new URLSearchParams({
            filter: filter,
            search: search,
            limit: limit.toString(),
            page: page.toString(),
        }).toString();
        
        const apiRoot = getApiUrl() || 'https://octopusworkapp.com:9001';
        const url = `${apiRoot}/tasks/getMyTasksNew?${params}`;
        
        const response = await apiGet(url);
        return response;
    } catch (error) {
        console.error('Get my tasks error:', error);
        throw error;
    }
};

export const getTaskComments = async (projectId, taskId) => {
    try {
        const token = getJwtToken();
        
        if (!token) {
            throw new Error('No JWT token available');
        }
        
        const apiRoot = getApiUrl() || 'https://octopusworkapp.com:9001';
        const url = `${apiRoot}/tasks/${projectId}/${taskId}/getComments`;
        
        const response = await apiGet(url);
        return response;
    } catch (error) {
        console.error('Get task comments error:', error);
        throw error;
    }
};

export const getTaskActivities = async (projectId, taskId) => {
    try {
        const token = getJwtToken();
        
        if (!token) {
            throw new Error('No JWT token available');
        }
        
        const apiRoot = getApiUrl() || 'https://octopusworkapp.com:9001';
        const url = `${apiRoot}/tasks/${projectId}/${taskId}/getActivities`;
        
        const response = await apiGet(url);
        return response;
    } catch (error) {
        console.error('Get task activities error:', error);
        throw error;
    }
};

/**
 * Create a comment on a task
 * @param {number} projectId - Project ID
 * @param {number} taskId - Task ID
 * @param {object} commentData - Comment data
 * @param {string} commentData.content - Comment content
 */
export const createTaskComment = async (projectId, taskId, commentData) => {
    try {
        const token = getJwtToken();
        
        if (!token) {
            throw new Error('No JWT token available');
        }
        
        const apiRoot = getApiUrl() || 'https://octopusworkapp.com:9001';
        const url = `${apiRoot}/tasks/${projectId}/${taskId}/createComment`;
        
        // If commentData is FormData, use fetch directly to properly handle multipart/form-data
        // Axios sometimes has issues with FormData and Content-Type boundaries
        if (commentData instanceof FormData) {
            const response = await fetch(url, {
                method: 'POST',
                headers: {
                    Authorization: `Bearer ${token}`,
                    // Don't set Content-Type - browser will set it with boundary
                },
                body: commentData,
            });
            
            const responseData = await response.json().catch(() => ({}));
            
            if (!response.ok || !responseData?.isSuccess) {
                throw new Error(responseData?.message || 'Failed to create comment');
            }
            
            return responseData;
        }
        
        // For non-FormData, use regular apiPost
        const response = await apiPost(url, commentData);
        return response;
    } catch (error) {
        console.error('Create task comment error:', error);
        throw error;
    }
};

/**
 * Delete a task
 * @param {number} projectId - Project ID
 * @param {number} taskId - Task ID
 */
export const deleteTask = async (projectId, taskListId, taskId) => {
    try {
        const token = getJwtToken();
        
        if (!token) {
            throw new Error('No JWT token available');
        }
        
        const apiRoot = getApiUrl() || 'https://octopusworkapp.com:9001';
        const url = `${apiRoot}/tasks/${projectId}/${taskId}/deleteTask`;
        
        const response = await apiPost(url, { delete: true, task_list_id: taskListId });
        return response;
    } catch (error) {
        console.error('Delete task error:', error);
        throw error;
    }
};

/**
 * Upload attachments to a task
 * @param {number} projectId - Project ID
 * @param {number} taskId - Task ID
 * @param {FormData} formData - FormData containing files
 */
export const uploadTaskAttachment = async (projectId, taskId, formData) => {
    try {
        const token = getJwtToken();
        
        if (!token) {
            throw new Error('No JWT token available');
        }
        
        const apiRoot = getApiUrl() || 'https://octopusworkapp.com:9001';
        const url = `${apiRoot}/tasks/${projectId}/${taskId}/uploadAttachment`;
        
        // Use fetch directly for FormData to properly handle multipart/form-data
        const response = await fetch(url, {
            method: 'POST',
            headers: {
                Authorization: `Bearer ${token}`,
                // Don't set Content-Type - browser will set it with boundary
            },
            body: formData,
        });
        
        const responseData = await response.json().catch(() => ({}));
        
        if (!response.ok || !responseData?.isSuccess) {
            throw new Error(responseData?.message || 'Failed to upload attachment');
        }
        
        return responseData;
    } catch (error) {
        console.error('Upload task attachment error:', error);
        throw error;
    }
};

/**
 * Delete a task attachment
 * @param {number} projectId - Project ID
 * @param {number} taskId - Task ID
 * @param {number} fileId - File ID to delete
 */
export const deleteTaskAttachment = async (projectId, taskId, fileId) => {
    try {
        const token = getJwtToken();
        
        if (!token) {
            throw new Error('No JWT token available');
        }
        
        const apiRoot = getApiUrl() || 'https://octopusworkapp.com:9001';
        const url = `${apiRoot}/tasks/${projectId}/${taskId}/deleteAttachment/${fileId}`;
        
        const response = await apiDelete(url);
        return response;
    } catch (error) {
        console.error('Delete task attachment error:', error);
        throw error;
    }
};

/**
 * Update a task comment
 * @param {number} projectId - Project ID
 * @param {number} taskId - Task ID
 * @param {number} commentId - Comment ID
 * @param {object} commentData - Comment data
 * @param {string} commentData.content - Comment content
 * @param {string} commentData.html_content - Comment HTML content
 */
export const updateTaskComment = async (projectId, taskId, commentId, commentData) => {
    try {
        const token = getJwtToken();
        
        if (!token) {
            throw new Error('No JWT token available');
        }
        
        const apiRoot = getApiUrl() || 'https://octopusworkapp.com:9001';
        const url = `${apiRoot}/tasks/${projectId}/${taskId}/updateComment/${commentId}`;
        
        // If commentData contains FormData, use fetch directly
        if (commentData instanceof FormData) {
            const response = await fetch(url, {
                method: 'PUT',
                headers: {
                    Authorization: `Bearer ${token}`,
                },
                body: commentData,
            });
            
            const responseData = await response.json().catch(() => ({}));
            
            if (!response.ok || !responseData?.isSuccess) {
                throw new Error(responseData?.message || 'Failed to update comment');
            }
            
            return responseData;
        }
        
        // Use apiPut for JSON data
        const response = await apiPut(url, commentData);
        
        // Check if response has isSuccess property
        if (response && response.isSuccess === false) {
            throw new Error(response.message || 'Failed to update comment');
        }
        
        return response;
    } catch (error) {
        console.error('Update task comment error:', error);
        // If error.response exists, extract the message
        if (error.response && error.response.data) {
            throw new Error(error.response.data.message || error.response.data.error || 'Failed to update comment');
        }
        throw error;
    }
};

/**
 * Delete a task comment
 * @param {number} projectId - Project ID
 * @param {number} taskId - Task ID
 * @param {number} commentId - Comment ID
 */
export const deleteTaskComment = async (projectId, taskId, commentId) => {
    try {
        const token = getJwtToken();
        
        if (!token) {
            throw new Error('No JWT token available');
        }
        
        const apiRoot = getApiUrl() || 'https://octopusworkapp.com:9001';
        const url = `${apiRoot}/tasks/${projectId}/${taskId}/deleteComment/${commentId}`;
        
        const response = await apiDelete(url);
        
        // Check if response has isSuccess property
        if (response && response.isSuccess === false) {
            throw new Error(response.message || 'Failed to delete comment');
        }
        
        return response;
    } catch (error) {
        console.error('Delete task comment error:', error);
        // If error.response exists, extract the message
        if (error.response && error.response.data) {
            throw new Error(error.response.data.message || error.response.data.error || 'Failed to delete comment');
        }
        throw error;
    }
};

export const getConversationMessages = async (conversationId, { limit = 50, page = 1 } = {}) => {
    try {
        const token = getJwtToken();
        
        if (!token) {
            throw new Error('No JWT token available');
        }
        const apiRoot = getApiUrl() || 'https://octopusworkapp.com:9001';
        
        // Using the thread messages endpoint
        const url = `${apiRoot}/messages/getThreadMessages/?threadId=${conversationId}&page=${page}&limit=${limit}`;
        
        const response = await apiGet(url);
        return response;
    } catch (error) {
        console.error('Get conversation messages error:', error);
        throw error;
    }
};

/**
 * Send message to a thread
 * @param {number} threadId - Thread ID
 * @param {object} data - Message data
 */
export const sendThreadMessage = async (threadId, data) => {
    try {
        const token = getJwtToken();
        
        if (!token) {
            throw new Error('No JWT token available');
        }
        $apiRoot = getApiUrl() || 'https://octopusworkapp.com:9001';
        const url = `${apiRoot}/messages/sendMessage`;
        
        const response = await apiPost(url, {
            thread_id: threadId,
            ...data
        });
        
        return response;
    } catch (error) {
        console.error('Send message error:', error);
        throw error;
    }
};

/**
 * Mark conversation as unread
 * @param {number} threadId - Thread ID
 */
export const markConversationUnread = async (threadId) => {
    try {
        const token = getJwtToken();
        
        if (!token) {
            throw new Error('No JWT token available');
        }
        
        const siteUrl = getSiteUrl();
        const url = `${siteUrl}/wp-json/buddyboss/v1/messages/action/${threadId}?action=unread&value=true`;
        
        const response = await apiGet(url);
        return response;
    } catch (error) {
        console.error('Mark unread error:', error);
        throw error;
    }
};

/**
 * Delete a single message
 * @param {number} messageId - Message ID
 */
export const deleteMessage = async (messageId) => {
    try {
        const token = getJwtToken();
        
        if (!token) {
            throw new Error('No JWT token available');
        }
        
        // Use the same API as React Native: POST /messages/deleteMessage/{messageId} with {delete:true}
        const apiRoot = getApiUrl() || 'https://octopusworkapp.com:9001';
        const url = `${apiRoot}/messages/deleteMessage/${messageId}`;
        
        const response = await apiPost(url, {
            delete: true
        });
        
        return response;
    } catch (error) {
        console.error('Delete message error:', error);
        throw error;
    }
};

/**
 * Add reaction to a message
 * @param {number} messageId - Message ID
 * @param {string} emoji - Emoji character
 */
export const addReaction = async (messageId, emoji) => {
    try {
        const token = getJwtToken();
        if (!token) {
            throw new Error('No JWT token available');
        }

        const apiRoot = getApiUrl() || 'https://octopusworkapp.com:9001';
        const url = `${apiRoot}/messages/addReaction/${messageId}`;
        return await apiPost(url, { emoji });
    } catch (error) {
        console.error('Add reaction error:', error);
        throw error;
    }
};

/**
 * Get reactions for a message
 * @param {number} messageId - Message ID
 */
export const getReactions = async (messageId) => {
    try {
        const token = getJwtToken();
        if (!token) {
            throw new Error('No JWT token available');
        }

        const apiRoot = getApiUrl() || 'https://octopusworkapp.com:9001';
        const url = `${apiRoot}/messages/getReactions/${messageId}`;
        return await apiGet(url);
    } catch (error) {
        console.error('Get reactions error:', error);
        throw error;
    }
};

/**
 * Edit an existing message
 * @param {number} messageId - Message ID
 * @param {object} data - Payload containing updated message text
 */
export const editMessage = async (messageId, data = {}) => {
    try {
        const token = getJwtToken();
        
        if (!token) {
            throw new Error('No JWT token available');
        }
        
        const apiRoot = getApiUrl() || 'https://octopusworkapp.com:9001';
        const url = `${apiRoot}/messages/editMessage/${messageId}`;
        
        const response = await apiPost(url, {
            message: data.message || '',
        });
        
        return response;
    } catch (error) {
        console.error('Edit message error:', error);
        throw error;
    }
};

/**
 * Search users for recipient selection (matches React Native API)
 * @param {string} search - Search query
 * @param {number} page - Page number
 * @param {number} size - Results per page (pageSize)
 */
export const searchUsers = async ({ search = '', page = 1, size = 20 } = {}) => {
    try {
        const token = getJwtToken();
        
        if (!token) {
            throw new Error('No JWT token available');
        }
        
        const apiRoot = getApiUrl() || 'https://octopusworkapp.com:9001';
        const url = `${apiRoot}/users/getUsers?page=${page}&size=${size}&search=${encodeURIComponent(search)}`;
        
        const response = await apiGet(url);
        
        // Match React Native response structure: Response.data.users.users and Response.data.users.pagination
        return response;
    } catch (error) {
        console.error('Search users error:', error);
        throw error;
    }
};

/**
 * Create a new conversation/message (matches React Native API)
 * @param {object} data - Message data
 * @param {array} data.users - Array of recipient user IDs (matches React Native: uses 'users' not 'recipients')
 * @param {string} data.message - Message content
 * @param {array} data.images - Array of image files
 * @param {array} data.videos - Array of video files
 * @param {array} data.documents - Array of document files
 */
export const createNewMessage = async (data) => {
    try {
        const token = getJwtToken();
        
        if (!token) {
            throw new Error('No JWT token available');
        }
        
        const apiRoot = getApiUrl() || 'https://octopusworkapp.com:9001';
        const url = `${apiRoot}/messages/sendNewMessage/`; // Matches React Native endpoint
        
        const formData = new FormData();
        formData.append('message', data.message || '');
        
        // Use 'users' field to match React Native (not 'recipients[]')
        if (data.users && Array.isArray(data.users)) {
            data.users.forEach((userId) => {
                formData.append('users', userId);
            });
        }
        
        const appendFiles = (items = [], fieldName) => {
            items.forEach((item, index) => {
                if (!item) return;
                if (item.file instanceof File || item.file instanceof Blob) {
                    formData.append(
                        fieldName,
                        item.file,
                        item.file.name || `${fieldName}-${Date.now()}-${index}`
                    );
                } else if (item instanceof File || item instanceof Blob) {
                    formData.append(fieldName, item, item.name || `${fieldName}-${Date.now()}-${index}`);
                }
            });
        };
        
        if (data.images) appendFiles(data.images, 'images');
        if (data.videos) appendFiles(data.videos, 'videos');
        if (data.documents) appendFiles(data.documents, 'documents');
        
        const response = await fetch(url, {
            method: 'POST',
            headers: {
                // Don't set Content-Type for FormData - browser will set it with boundary
                Authorization: `Bearer ${token}`,
            },
            body: formData,
        });
        
        const responseData = await response.json().catch(() => ({}));
        
        if (!response.ok || !responseData?.isSuccess) {
            throw new Error(responseData?.message || 'Failed to create message');
        }
        
        // Response structure: Response.data.thread_id and Response.data.message_id
        return responseData;
    } catch (error) {
        console.error('Create new message error:', error);
        throw error;
    }
};

